// src/pages/UbicacionPage.jsx

import React from "react";
import { motion } from "framer-motion";

import PageTransition from "./Pagetransition";

/* ───────────────── ICONS ───────────────── */

const pinIcon = (
  <svg
    style={{ width: 24, height: 24 }}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
    <circle cx="12" cy="9" r="2.5" />
  </svg>
);

const phoneIcon = (
  <svg
    style={{ width: 24, height: 24 }}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.01 1.18 2 2 0 012 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 14.92z" />
  </svg>
);

const clockIcon = (
  <svg
    style={{ width: 24, height: 24 }}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12,6 12,12 16,14" />
  </svg>
);

/* ───────────────── ANIMATIONS ───────────────── */

const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.15,
    },
  },
};

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40,
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

/* ───────────────── INFO ROW ───────────────── */

function InfoRow({ icon, label, children }) {
  return (
    <motion.div
      variants={fadeUp}
      whileHover={{
        x: 6,
      }}
      transition={{
        duration: 0.35,
      }}
      className="
        group
        relative
        overflow-hidden
        border
        border-[#c9973a]/10
        bg-white/[0.03]
        p-5
        backdrop-blur-xl
      "
    >
      {/* Glow */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#c9973a]/5 via-transparent to-[#8b3520]/10 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

      <div className="relative z-10 flex items-start gap-5">
        {/* Icon */}
        <div
          className="
            flex
            h-14
            w-14
            flex-shrink-0
            items-center
            justify-center
            border
            border-[#c9973a]/20
            bg-gradient-to-br
            from-[#c9973a]/10
            to-[#8b3520]/10
            text-[#f0c96b]
          "
        >
          {icon}
        </div>

        {/* Content */}
        <div>
          <h3
            className="
              text-[0.78rem]
              uppercase
              tracking-[0.28em]
              text-[#c9973a]
            "
            style={{
              fontFamily: "'Jost', sans-serif",
            }}
          >
            {label}
          </h3>

          <p
            className="
              mt-3
              text-[1rem]
              leading-8
              text-[#f9f5ef]
            "
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 400,
            }}
          >
            {children}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

/* ───────────────── PAGE ───────────────── */

export default function UbicacionPage() {
  return (
    <PageTransition>
      <section className="relative min-h-screen overflow-hidden bg-[#0f0d0b] text-white">
        {/* GRID BACKGROUND */}
        <div
          className="
            absolute inset-0
            bg-[linear-gradient(rgba(201,151,58,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(201,151,58,0.04)_1px,transparent_1px)]
            bg-[size:60px_60px]
          "
        />

        {/* RADIAL GLOW */}
        <motion.div
          animate={{
            scale: [1, 1.08, 1],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="
            absolute
            left-1/2
            top-1/2
            h-[42rem]
            w-[42rem]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            bg-[radial-gradient(circle,rgba(201,151,58,0.12)_0%,transparent_70%)]
          "
        />

        {/* TOP GLOW */}
        <div className="absolute left-[-10rem] top-[-10rem] h-[30rem] w-[30rem] rounded-full bg-[#c9973a]/10 blur-[120px]" />

        {/* BOTTOM GLOW */}
        <div className="absolute bottom-[-12rem] right-[-10rem] h-[35rem] w-[35rem] rounded-full bg-[#8b3520]/20 blur-[140px]" />

        {/* CONTENT */}
        <div className="relative z-10 mx-auto max-w-7xl px-5 pb-24 pt-28 md:px-8 lg:pt-36">
          {/* HERO */}
          <motion.div
            initial={{
              opacity: 0,
              y: 40,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 1,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="mx-auto max-w-4xl text-center"
          >
            {/* Eyebrow */}
            <div
              className="
                mb-6
                inline-flex
                items-center
                gap-3
                border
                border-[#c9973a]/20
                bg-[#c9973a]/5
                px-5
                py-2
              "
            >
              <div className="h-2 w-2 rounded-full bg-[#c9973a]" />

              <span
                className="
                  text-[0.68rem]
                  uppercase
                  tracking-[0.45em]
                  text-[#c9973a]
                "
                style={{
                  fontFamily: "'Jost', sans-serif",
                }}
              >
                Santa Clara del Mar · Buenos Aires
              </span>
            </div>

            {/* Title */}
            <h1
              className="
                text-[4rem]
                leading-[0.9]
                text-[#f9f5ef]
                md:text-[7rem]
              "
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 300,
              }}
            >
              Nuestra
              <br />

              <span
                className="
                  bg-gradient-to-r
                  from-[#f0c96b]
                  via-[#c9973a]
                  to-[#fde68a]
                  bg-clip-text
                  text-transparent
                "
                style={{
                  fontWeight: 700,
                }}
              >
                Ubicación
              </span>
            </h1>

            {/* Subtitle */}
            <p
              className="
                mx-auto
                mt-8
                max-w-3xl
                text-[1rem]
                leading-9
                text-[#b7aea2]
                md:text-[1.1rem]
              "
              style={{
                fontFamily: "'Jost', sans-serif",
                fontWeight: 300,
              }}
            >
              Te esperamos en nuestro espacio para asesorarte
              personalmente y ayudarte a encontrar la mejor
              solución para tu proyecto.
            </p>
          </motion.div>

          {/* GRID */}
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{
              once: true,
              amount: 0.15,
            }}
            className="
              mt-20
              grid
              grid-cols-1
              overflow-hidden
              border
              border-[#c9973a]/10
              bg-white/[0.03]
              backdrop-blur-xl
              lg:grid-cols-[480px_1fr]
            "
          >
            {/* LEFT PANEL */}
            <div className="relative overflow-hidden border-b border-[#c9973a]/10 p-8 md:p-12 lg:border-b-0 lg:border-r">
              {/* Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#c9973a]/10 via-transparent to-[#8b3520]/20" />

              <div className="relative z-10">
                <motion.div variants={fadeUp}>
                  <span
                    className="
                      text-[0.72rem]
                      uppercase
                      tracking-[0.35em]
                      text-[#c9973a]
                    "
                    style={{
                      fontFamily: "'Jost', sans-serif",
                    }}
                  >
                    Información de contacto
                  </span>

                  <h2
                    className="
                      mt-5
                      text-[3rem]
                      leading-none
                      text-[#f9f5ef]
                      md:text-[4rem]
                    "
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontWeight: 400,
                    }}
                  >
                    Encontranos aquí
                  </h2>

                  <div className="mt-6 h-px w-24 bg-gradient-to-r from-[#c9973a] to-transparent" />
                </motion.div>

                {/* INFO */}
                <div className="mt-10 space-y-5">
                  <InfoRow
                    icon={pinIcon}
                    label="Dirección"
                  >
                    Saint James 998
                    <br />
                    Santa Clara del Mar, Buenos Aires
                  </InfoRow>

                  <InfoRow
                    icon={phoneIcon}
                    label="WhatsApp"
                  >
                    +54 2236 90-1020
                  </InfoRow>

                  <InfoRow
                    icon={clockIcon}
                    label="Horario"
                  >
                    Lunes a Sábados
                    <br />
                    09:00 hs · 18:00 hs
                  </InfoRow>
                </div>

                {/* BUTTON */}
                <motion.a
                  variants={fadeUp}
                  href="https://wa.me/542236901020"
                  target="_blank"
                  rel="noreferrer"
                  whileHover={{
                    y: -3,
                    boxShadow:
                      "0 18px 50px rgba(201,151,58,0.25)",
                  }}
                  whileTap={{
                    scale: 0.97,
                  }}
                  className="
                    group
                    relative
                    mt-10
                    inline-flex
                    overflow-hidden
                  "
                >
                  {/* bg */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[#8b6220] via-[#c9973a] to-[#f0c96b]" />

                  {/* shine */}
                  <motion.div
                    animate={{
                      x: ["-120%", "220%"],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    className="
                      absolute
                      inset-y-0
                      w-24
                      rotate-12
                      bg-white/20
                      blur-xl
                    "
                  />

                  <div
                    className="
                      relative
                      px-8
                      py-5
                      text-[#1a1814]
                    "
                  >
                    <span
                      className="
                        text-[0.78rem]
                        font-semibold
                        uppercase
                        tracking-[0.28em]
                      "
                      style={{
                        fontFamily: "'Jost', sans-serif",
                      }}
                    >
                      Escribinos por WhatsApp
                    </span>
                  </div>
                </motion.a>
              </div>
            </div>

            {/* MAP */}
            <motion.div
              variants={fadeUp}
              className="relative min-h-[500px] overflow-hidden"
            >
              {/* overlay */}
              <div className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-r from-[#0f0d0b]/20 via-transparent to-transparent" />

              {/* map glow */}
              <div className="absolute left-0 top-0 z-10 h-32 w-full bg-gradient-to-b from-[#0f0d0b]/50 to-transparent" />

              <iframe
                src="https://www.google.com/maps?q=Saint+James+998,+Santa+Clara+del+Mar,+Buenos+Aires,+Argentina&output=embed"
                width="100%"
                height="100%"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Monaco Aberturas - Saint James 998, Santa Clara del Mar"
                className="
                  absolute
                  inset-0
                  h-full
                  w-full
                  border-0
                  grayscale
                  contrast-125
                  brightness-[0.85]
                "
              />
            </motion.div>
          </motion.div>
        </div>
      </section>
    </PageTransition>
  );
}